// ============================================
// useAuth.js - Authentication Hook
// ============================================
// React hook for managing authentication state
// Handles login, register, logout, and user data

import { useState, useEffect, useCallback } from 'react';
import {
  checkAuth,
  login as authLogin,
  register as authRegister,
  logout as authLogout,
  fetchUser,
} from '../services/authService';
import { initializeDevice, getUsageStatus } from '../services/chatService';

/**
 * Authentication hook
 * @returns {Object} Auth state and methods
 */
export function useAuth() {
  const [user, setUser] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check auth status on mount
  useEffect(() => {
    const initialize = async () => {
      setLoading(true);

      try {
        // Initialize device fingerprint
        await initializeDevice();

        // Check if already authenticated
        const { isAuthenticated, user: storedUser } = checkAuth();

        if (isAuthenticated) {
          // Fetch fresh user data
          const { user: freshUser, error } = await fetchUser();
          if (freshUser) {
            setUser(freshUser);
          } else if (storedUser) {
            setUser(storedUser);
          }
          if (error) {
            console.warn('Failed to fetch user:', error);
          }
        }

        // Get usage status (works for both auth and anon)
        const { usage } = await getUsageStatus();
        if (usage) {
          setUsage(usage);
        }
      } catch (e) {
        console.error('Auth initialization failed:', e);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, []);

  // Login
  const login = useCallback(async (email, password) => {
    setError(null);

    const result = await authLogin(email, password);

    if (result.success) {
      setUser(result.user);
      // Refresh usage after login
      const { usage } = await getUsageStatus();
      setUsage(usage);
      return { success: true };
    }

    setError(result.error);
    return { success: false, error: result.error };
  }, []);

  // Register
  const register = useCallback(async (data) => {
    setError(null);

    const result = await authRegister(data);

    if (result.success) {
      setUser(result.user);
      // Refresh usage after register
      const { usage } = await getUsageStatus();
      setUsage(usage);
      return { success: true };
    }

    setError(result.error);
    return { success: false, error: result.error };
  }, []);

  // Logout
  const logout = useCallback(() => {
    authLogout();
    setUser(null);
    // Refresh usage (will now show anon status)
    getUsageStatus().then(({ usage }) => setUsage(usage));
  }, []);

  // Refresh user data
  const refreshUser = useCallback(async () => {
    const { user: freshUser } = await fetchUser();
    if (freshUser) {
      setUser(freshUser);
    }
  }, []);

  // Refresh usage
  const refreshUsage = useCallback(async () => {
    const { usage } = await getUsageStatus();
    if (usage) {
      setUsage(usage);
    }
  }, []);

  return {
    // State
    user,
    usage,
    loading,
    error,
    isLoggedIn: !!user,

    // Methods
    login,
    register,
    logout,
    refreshUser,
    refreshUsage,
    clearError: () => setError(null),
  };
}

export default useAuth;
